/* eslint-disable react-hooks/exhaustive-deps */
import { Button, ButtonPrimary } from '@/components/button'
import { MainContent } from '@/components/content'
import { ArrowBack } from '@/components/icons'
import { DatePickerInput, NumberInput, PriceInput, Select, TextInput, TimezoneSelect } from '@/components/input'
import { MainLayout } from '@/components/layouts'
import { MainSidebar, MobileSidebar } from '@/components/sidebar'
import { bracketFormatOptions, eliminationOptions, playerGroupOptions } from '@/constants/DropdownOptionsConstants'
import { APP_CUSTOM_ERROR } from '@/constants/MessageConstants'
import { timeZoneList } from '@/constants/Timezones'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { getClubDetail } from '@/pages/club/clubSlice'
import { clubSelector } from '@/pages/club/selectors'
import { appSelector } from '@/pages/home/selectors'
import { useSaveBracket } from '@/services'
import { EventFormat, PlayerGroup } from '@/types'
import AddBracketValidationSchema from '@/utils/schema/AddBracketValidationSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { eventSelector } from '../selectors'

export type AddBracketForm = {
  bracketId?: number
  leagueId?: number
  clubId?: number
  name: string
  elimination: string
  format: EventFormat | string
  playerGroup: PlayerGroup | string
  minAge: string
  maxAge: string
  minRating: string
  maxRating: string
  registrationStartDateTime: string
  registrationEndDateTime: string
  competitionStartDateTime: string
  competitionEndDateTime: string
  memberPrice: string
  nonMemberPrice: string
  courts: number
  maxTeam: string
  waitList: string
  timeZone: string
  zoneName: string
}

export const AddBracket = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const params = useParams()

  const dispatch = useAppDispatch()
  const { isShowDropdown } = useAppSelector(appSelector)
  const { event } = useAppSelector(eventSelector)
  const { club } = useAppSelector(clubSelector)

  const { eventId } = params

  const [disableTeams, setDisableTeams] = useState(false)
  const [nextButtonEnable, setNextButtonEnable] = useState(false)

  const { mutate: saveBracket } = useSaveBracket()

  const isInternationalClub = club?.accountStatus?.accountId === 1

  const {
    setValue,
    control,
    handleSubmit,
    register,
    watch,
    trigger,
    formState: { errors },
  } = useForm<AddBracketForm>({
    defaultValues: {
      name: '',
      elimination: '',
      minAge: '',
      maxAge: '',
      minRating: '',
      maxRating: '',
      playerGroup: '',
      format: '',
      registrationStartDateTime: '',
      registrationEndDateTime: '',
      competitionStartDateTime: '',
      competitionEndDateTime: '',
      memberPrice: '',
      nonMemberPrice: '',
      courts: 0,
      maxTeam: '',
      waitList: '',
      timeZone: '',
      zoneName: '',
    },
    resolver: yupResolver(AddBracketValidationSchema as any),
    mode: 'onTouched',
  })

  const watchElimination = watch('elimination')

  const onGoBack = () => navigate(-1)

  const onSubmit = handleSubmit(async (data) => {
    setNextButtonEnable(true)
    if (eventId) {
      const payload = {
        leagueId: parseInt(eventId, 10),
        ageBracket: [data.minAge ? parseInt(data.minAge, 10) : null, data.maxAge ? parseInt(data.maxAge, 10) : null],
        durationDateTime: [
          data.competitionStartDateTime ? moment(data.competitionStartDateTime).format('YYYY-MM-DDTHH:mm:ss') : null,
          data.competitionEndDateTime ? moment(data.competitionEndDateTime).format('YYYY-MM-DDTHH:mm:ss') : null,
        ],
        format: data.format,
        maxTeam: parseInt(data.maxTeam, 10),
        name: data.name,
        elimination: data.elimination,
        memberFee: parseFloat(data.memberPrice).toFixed(2),
        nonMemberFee: parseFloat(data.nonMemberPrice).toFixed(2),
        playerGroup: data.playerGroup,
        ratingBracket: [
          data.minRating ? parseFloat(data.minRating) : null,
          data.maxRating ? parseFloat(data.maxRating) : null,
        ],
        courts: data.courts,
        timeZone: data.timeZone,
        zoneName: data.zoneName,
        registrationDateTime: [
          data.registrationStartDateTime ? moment(data.registrationStartDateTime).format('YYYY-MM-DDTHH:mm:ss') : null,
          data.registrationEndDateTime ? moment(data.registrationEndDateTime).format('YYYY-MM-DDTHH:mm:ss') : null,
        ],
        waitList: parseInt(data.waitList, 10),
      }
      saveBracket(payload, {
        onSuccess: () => {
          onGoBack()
          setNextButtonEnable(false)
          toast.success(t('event.bracketCreated'))
        },
        onError: (error: any) => {
          setNextButtonEnable(false)
          toast.error(error?.data?.message || APP_CUSTOM_ERROR)
        },
      })
    }
  })

  const autoGenerateName = () => {
    const watchBracketType = watch('format')
    const watchPlayerGroup = watch('playerGroup')
    const watchAgeRangeMin = watch('minAge')
    const watchAgeRangeMax = watch('maxAge')
    const watchRatingRangeMin = watch('minRating')
    const watchRatingRangeMax = watch('maxRating')
    const name = `${watchPlayerGroup} ${watchBracketType} - ${watchRatingRangeMin}-${watchRatingRangeMax}:${watchAgeRangeMin}-${watchAgeRangeMax}`
    setValue('name', name, { shouldValidate: true })
  }

  const onEliminationChange = (value: string) => {
    value &&
      setValue('elimination', value, {
        shouldDirty: true,
        shouldValidate: true,
      })

    const eliminationType = watch('elimination')
    setValue('courts', 0)
    if (eliminationType === 'ROUND_ROBIN') {
      setValue('maxTeam', '0', { shouldDirty: false, shouldValidate: false })
    } else if (eliminationType === 'FLEX') {
      //   setFlexLeagueWarning(true)
      setValue('maxTeam', '0', { shouldDirty: false, shouldValidate: false })
    } else if (eliminationType === 'COMPASS') {
      setValue('maxTeam', '16', { shouldDirty: false, shouldValidate: false })
    }
  }

  useEffect(() => {
    if (watchElimination === 'COMPASS') {
      setDisableTeams(true)
    } else {
      setDisableTeams(false)
    }
  }, [watchElimination])

  useEffect(() => {
    if (event) dispatch(getClubDetail(event?.clubId.toString()))
  }, [event])

  return (
    <>
      {!isShowDropdown && <MobileSidebar />}
      <MainLayout>
        <MainSidebar />
        <MainContent>
          <div className="flex flex-col mb-20 lg:mb-8">
            <div className="flex items-center justify-between">
              <div className="flex gap-x-3 items-center">
                <Button onClick={onGoBack} className="hover:opacity-50">
                  <ArrowBack className="text-white" />
                </Button>
                <h1 className="text-sm md:text-xl text-white font-semibold">{t('event.addABracket')}</h1>
              </div>
              <ButtonPrimary
                title={nextButtonEnable ? `${t('common.publishing')}...` : t('common.publish')}
                className="py-3 px-5"
                onClick={onSubmit}
                disabled={nextButtonEnable}
              />
            </div>

            <div className="bg-white rounded-2xl py-4 px-6 flex flex-col mt-4 mb-6">
              <div className="flex flex-col gap-y-6 mt-6">
                <div className="flex flex-col gap-y-4">
                  <div className="flex flex-col gap-y-2 sm:flex-row sm:gap-x-3 xl:gap-x-4">
                    <Controller
                      name={'elimination'}
                      control={control}
                      render={({ field }) => (
                        <Select
                          label={t('common.type')}
                          placeholder={t('common.select')}
                          labelClassName="!text-navy-900"
                          onChange={(value) => onEliminationChange(value)}
                          value={eliminationOptions.find((opt) => opt.value === field.value)}
                          options={eliminationOptions}
                          error={errors?.elimination?.message}
                        />
                      )}
                    />
                    <Controller
                      name={'playerGroup'}
                      control={control}
                      render={({ field }) => (
                        <Select
                          label={t('common.playerGroup')}
                          placeholder={t('common.select')}
                          labelClassName="!text-navy-900"
                          value={playerGroupOptions.find((opt) => opt.value === field.value)}
                          onChange={(value) => {
                            value &&
                              setValue('playerGroup', value, {
                                shouldDirty: true,
                                shouldValidate: true,
                              })
                            if (watch('format')) {
                              trigger('format', { shouldFocus: false })
                            }
                          }}
                          options={playerGroupOptions}
                          error={errors?.playerGroup?.message}
                        />
                      )}
                    />
                    <Controller
                      name={'format'}
                      control={control}
                      render={({ field }) => (
                        <Select
                          label={t('common.format')}
                          placeholder={t('common.select')}
                          labelClassName="!text-navy-900"
                          value={bracketFormatOptions.find((opt) => opt.value === field.value)}
                          onChange={(value) =>
                            value &&
                            setValue(`format`, value, {
                              shouldDirty: true,
                              shouldTouch: true,
                              shouldValidate: true,
                            })
                          }
                          options={bracketFormatOptions}
                          error={errors?.format?.message}
                        />
                      )}
                    />
                  </div>

                  {/* Age range */}
                  <div className="flex flex-col gap-y-2">
                    <span className="text-navy-900 text-sm font-semibold">{t('common.ageRange')}</span>
                    <div className="flex flex-col md:flex-row md:items-baseline gap-2">
                      <div className="md:flex-1 flex-col gap-y-2">
                        <NumberInput
                          label={t('common.minAge')}
                          labelClassName="!text-navy-900 !text-xs"
                          min={2}
                          {...register('minAge')}
                          error={errors?.minAge?.message}
                        />
                      </div>
                      <div className="hidden md:block translate-y-11">-</div>
                      <div className="md:flex-1 flex-col gap-y-2">
                        <NumberInput
                          label={t('common.maxAge')}
                          labelClassName="!text-navy-900 !text-xs"
                          min={2}
                          {...register('maxAge')}
                          error={errors?.maxAge?.message}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Rating range */}
                  <div className="flex flex-col gap-y-2">
                    <span className="text-navy-900 text-sm font-semibold">{t('common.ratingRange')}</span>
                    <div className="flex flex-col md:flex-row md:items-baseline gap-2">
                      <div className="md:flex-1 flex-col gap-y-2">
                        <NumberInput
                          label={t('common.minimum')}
                          labelClassName="!text-navy-900 !text-xs"
                          min={2}
                          max={8}
                          {...register('minRating')}
                          error={errors?.minRating?.message}
                        />
                      </div>
                      <div className="hidden md:block translate-y-11">-</div>
                      <div className="md:flex-1 flex-col gap-y-2">
                        <NumberInput
                          label={t('common.maximum')}
                          labelClassName="!text-navy-900 !text-xs"
                          min={2}
                          max={8}
                          {...register('maxRating')}
                          error={errors?.maxRating?.message}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Bracket name */}
                  <div className="flex flex-col gap-y-2">
                    <div className="flex flex-col md:flex-row items-center gap-2">
                      <TextInput
                        className="w-full"
                        labelClassName="!text-navy-900"
                        label={t('common.name')}
                        placeholder={t('common.bracketName')}
                        {...register('name')}
                        error={errors?.name?.message}
                      />
                      <ButtonPrimary
                        title={t('common.autoGenerate')}
                        className={`w-full md:w-auto whitespace-nowrap ${errors?.name ? 'mt-0' : 'mt-0 sm:mt-7'}`}
                        disabled={
                          !watch('format') ||
                          !watch('playerGroup') ||
                          !watch('minAge') ||
                          !watch('maxAge') ||
                          !watch('minRating') ||
                          !watch('maxRating') ||
                          !!watch('name')
                        }
                        onClick={() => autoGenerateName()}
                      />
                    </div>
                  </div>

                  <NumberInput
                    label={t('common.courts')}
                    description={t('common.courtsDesc')}
                    min={1}
                    max={8}
                    labelClassName="!text-navy-900"
                    descriptionClassName="!text-navy-900"
                    disabled={!disableTeams}
                    {...register('courts')}
                    error={errors?.courts?.message}
                  />

                  <NumberInput
                    label={t('common.maxTeams')}
                    description={t('common.maxTeamsDec')}
                    min={2}
                    labelClassName="!text-navy-900"
                    disabled={disableTeams}
                    descriptionClassName="!text-navy-900"
                    {...register('maxTeam')}
                    error={errors?.maxTeam?.message}
                  />

                  <NumberInput
                    label={t('common.waitList')}
                    description={t('common.waitListDesc')}
                    min={0}
                    labelClassName="!text-navy-900"
                    descriptionClassName="!text-navy-900"
                    {...register('waitList')}
                    error={errors?.waitList?.message}
                  />
                </div>

                {/* Registration Date */}
                <div className="flex flex-col gap-y-4">
                  <div className="flex flex-col">
                    <span className="text-navy-900 text-base font-semibold">{t('common.registration')}</span>
                    <span className="text-navy-900 text-xs">{t('common.registrationDesc')}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-x-6">
                    <div className="flex-1 flex flex-col gap-y-2">
                      <DatePickerInput
                        label={t('common.startDateTime')}
                        labelClassName="!text-navy-900"
                        selected={watch('registrationStartDateTime')}
                        showTimeInput
                        timeInputLabel="Time:"
                        dateFormat="MMM dd, yyyy h:mm aa"
                        onChange={(date: Date | null) => {
                          date &&
                            setValue('registrationStartDateTime', moment(date).format('YYYY-MM-DDTHH:mm:ss'), {
                              shouldDirty: true,
                              shouldValidate: true,
                            })
                        }}
                        error={errors?.registrationStartDateTime?.message}
                      />
                    </div>
                    <div className="flex-1 flex flex-col gap-y-2">
                      <DatePickerInput
                        label={t('common.endDateTime')}
                        labelClassName="!text-navy-900"
                        selected={watch('registrationEndDateTime')}
                        showTimeInput
                        timeInputLabel="Time:"
                        dateFormat="MMM dd, yyyy h:mm aa"
                        onChange={(date: Date | null) => {
                          date &&
                            setValue('registrationEndDateTime', moment(date).format('YYYY-MM-DDTHH:mm:ss'), {
                              shouldDirty: true,
                              shouldValidate: true,
                            })
                        }}
                        error={errors?.registrationEndDateTime?.message}
                      />
                    </div>
                  </div>
                </div>

                {/* Competition Date */}
                <div className="flex flex-col gap-y-4">
                  <div className="flex flex-col">
                    <span className="text-navy-900 text-base font-semibold">{t('common.competition')}</span>
                    <span className="text-navy-900 text-xs">{t('common.competitionDesc')} </span>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-x-6">
                    <div className="flex-1 flex flex-col gap-y-2">
                      <DatePickerInput
                        label={t('common.startDateTime')}
                        labelClassName="!text-navy-900"
                        selected={watch('competitionStartDateTime')}
                        showTimeInput
                        timeInputLabel="Time:"
                        dateFormat="MMM dd, yyyy h:mm aa"
                        onChange={(date: Date | null) => {
                          date &&
                            setValue('competitionStartDateTime', moment(date).format('YYYY-MM-DDTHH:mm:ss'), {
                              shouldDirty: true,
                              shouldValidate: true,
                            })
                        }}
                        error={errors?.competitionStartDateTime?.message}
                      />
                    </div>
                    <div className="flex-1 flex flex-col gap-y-2">
                      <DatePickerInput
                        label={t('common.endDateTime')}
                        labelClassName="!text-navy-900"
                        selected={watch('competitionEndDateTime')}
                        showTimeInput
                        timeInputLabel="Time:"
                        dateFormat="MMM dd, yyyy h:mm aa"
                        onChange={(date: Date | null) => {
                          date &&
                            setValue('competitionEndDateTime', moment(date).format('YYYY-MM-DDTHH:mm:ss'), {
                              shouldDirty: true,
                              shouldValidate: true,
                            })
                        }}
                        error={errors?.competitionEndDateTime?.message}
                      />
                    </div>
                  </div>

                  <Controller
                    name={`timeZone`}
                    control={control}
                    render={({ field }) => (
                      <TimezoneSelect
                        label={t('common.timeZone')}
                        labelClassName="!text-navy-900"
                        onBlur={field.onBlur}
                        onChange={(timeZone: string, zoneName: string) => {
                          timeZone &&
                            setValue('timeZone', timeZone, {
                              shouldDirty: true,
                              shouldValidate: true,
                            })
                          zoneName &&
                            setValue('zoneName', zoneName, {
                              shouldDirty: true,
                              shouldValidate: true,
                            })
                        }}
                        value={timeZoneList.find((opt) => opt.value === field.value)}
                        error={errors?.timeZone?.message}
                      />
                    )}
                  />
                </div>

                <div className="flex flex-col gap-y-4">
                  <div className="flex flex-col">
                    <span className="text-navy-900 text-base font-semibold">{t('common.price')}</span>
                    <span className="text-navy-900 text-xs">{t('common.priceDesc')}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-x-6">
                    <PriceInput
                      label={t('common.memberPrice')}
                      labelClassName="!text-navy-900"
                      prefix={event?.currencyDetails?.currencySymbol}
                      {...register(`memberPrice`)}
                      error={errors?.memberPrice?.message}
                      disabled={isInternationalClub}
                    />
                    <PriceInput
                      label={t('common.nonMemberPrice')}
                      labelClassName="!text-navy-900"
                      prefix={event?.currencyDetails?.currencySymbol}
                      {...register(`nonMemberPrice`)}
                      error={errors?.nonMemberPrice?.message}
                      disabled={isInternationalClub}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </MainContent>
      </MainLayout>
    </>
  )
}
